import type { MessageDescriptor } from '@lingui/core';
import { msg } from '@lingui/core/macro';
import { z } from 'zod';

import { filterAction } from '@/shared/filters/filter-action';
import { listPageAction } from '@/shared/patterns/list-search';
import { action, can, defineScreen } from '@/shared/screen/define';

import { preparedAction } from './beneficiary-limits.screen';
import { pixListFilters } from './pix.filters';
import { BANK_ACCOUNT_LIST_PATH } from './bank-account.search';

// Ações da aba Pix e a tela Gerenciar chaves (U3, U23/U24). Leve: o fio entra pela tela.
//
// O agente **só prepara**: Transferir por Pix, Copia e cola, Receber Pix QR Code, Aprovar, Reprovar,
// Cadastrar chave e os pedidos de portabilidade abrem o formulário ou o diálogo em branco; nenhuma chave,
// documento, conta, valor ou código copia e cola passa pela ferramenta. Ficam fora, de propósito: Cancelar,
// Comprovante, Detalhes e Excluir chave (presos a uma linha: precisariam de chave de alvo, que a trava
// `agent-tool-targets` recusa) e Exportar PDF (feito só na tela).

// eslint-disable-next-line lingui/no-unlocalized-strings -- resposta ao modelo, inglês canônico (U33)
const OPEN_SCREEN_ONLY = 'This action only runs on the screen that is open, on the bank account open there.';
const needsOpenScreen = () => {
  throw new Error(OPEN_SCREEN_ONLY);
};

/**
 * Abre um formulário em branco sem senha nem código (o legado não pede step-up aqui): é rascunho (`draft`), a
 * ferramenta não envia nada e quem preenche e confirma na tela é a pessoa.
 */
const openOnly = (id: string, label: MessageDescriptor, description: MessageDescriptor) =>
  action({
    id,
    kind: 'draft',
    label,
    description,
    input: z.strictObject({}),
    run: needsOpenScreen,
  });

// ─── Aba Pix ──────────────────────────────────────────────────────────────────────────────────────

/** "Atualizar" da tabela (`TransferHeaderList.tsx:21-24`): a lista e o saldo da conta. */
export const pixRefresh = action({
  id: 'bankAccount.pix.refresh',
  kind: 'read',
  label: msg`Atualizar`,
  description: msg`Busca de novo a lista de transferências Pix e o saldo da conta aberta. Nada é alterado.`,
  run: () => undefined,
});

export const pixFilter = filterAction('bankAccount.pix.filter', pixListFilters, {
  description: msg`Filtra a lista de transferências Pix da conta aberta: pix_searchString (todos os campos), pix_transferDate (data da ocorrência, AAAA-MM-DD), pix_beneficiaryName (nome do favorecido), pix_beneficiaryRegistrationNumber (CPF/CNPJ, dígitos e letras) e pix_status (Approved, Pending, Rejected, Timeout, Canceled, Error, Processing, Finished). Filtro omitido é limpo.`,
});

export const pixPage = listPageAction('bankAccount.pix.page');

/** A paginação da lista Pix na conta transitória (somente leitura), no manifesto dela. */
export const transitoryPixPage = listPageAction('bankAccount.transitory.pix.page');

/** "Transferir por Pix" (`HeaderTransfer.tsx:59-65`): favorecido, valor e data, com senha e código MFA. */
export const pixCreate = preparedAction(
  'bankAccount.pix.create',
  msg`Transferir por Pix`,
  'write',
  msg`Abre o formulário Transferir por Pix da conta aberta, em branco. Quem escolhe o favorecido ou digita a chave, o valor e a data e confirma com a senha e o código MFA é a pessoa: esta ferramenta não transfere nada. Não recebe entrada.`,
  msg`Transferir por Pix`,
);

/** "Copia e cola" (`HeaderTransfer.tsx:66-70`): a pessoa cola o código, confere e confirma com senha e código MFA. */
export const pixCopyPaste = preparedAction(
  'bankAccount.pix.copyPaste',
  msg`Copia e cola`,
  'write',
  msg`Abre o formulário Pix copia e cola da conta aberta, em branco. Quem cola o código, confere o recebedor e o valor e confirma com a senha e o código MFA é a pessoa: esta ferramenta não paga nada. Não recebe entrada.`,
  msg`Pix copia e cola`,
);

/** "Receber Pix QR Code" (`HeaderTransfer.tsx:71-75`): sem step-up no legado; a ferramenta só abre o formulário. */
export const pixReceiveQrCode = openOnly(
  'bankAccount.pix.receiveQrCode',
  msg`Receber Pix QR Code`,
  msg`Abre o formulário Receber Pix QR Code da conta aberta, em branco. Quem escolhe a chave, o tipo do QR Code e o valor e gera o código é a pessoa: esta ferramenta não gera nada. Não recebe entrada.`,
);

/** "Aprovar" e "Reprovar" do cabeçalho da tabela: pedem senha e código MFA. */
export const pixApprove = preparedAction(
  'bankAccount.pix.approve',
  msg`Aprovar`,
  'write',
  msg`Prepara a aprovação das transferências Pix marcadas na lista da conta aberta. Só abre o diálogo de senha e código MFA, com a quantidade e o total: esta ferramenta não aprova nada. Não recebe entrada; os alvos são as linhas marcadas na página aberta, todas aguardando aprovação.`,
  msg`Aprovar transferência`,
);

export const pixReject = preparedAction(
  'bankAccount.pix.reject',
  msg`Reprovar`,
  'destructive',
  msg`Prepara a reprovação das transferências Pix marcadas na lista da conta aberta. Só abre o diálogo de senha e código MFA, com a quantidade e o total: esta ferramenta não reprova nada. Não recebe entrada; os alvos são as linhas marcadas na página aberta, todas aguardando aprovação.`,
  msg`Rejeitar transferência`,
);

/** As ações novas do detalhe, para o `actions` do manifesto. */
export const PIX_ACTIONS = [pixRefresh, pixFilter, pixPage, pixCreate, pixCopyPaste, pixReceiveQrCode, pixApprove, pixReject];

// ─── Gerenciar chaves ─────────────────────────────────────────────────────────────────────────────

/** `/pix/gerenciar-chaves` (`router.tsx:475-483`): não é aba, é uma tela filha do detalhe. */
export const bankAccountPixKeysPath = (id: string) => `${BANK_ACCOUNT_LIST_PATH}/${encodeURIComponent(id)}/pix/gerenciar-chaves`;

/** "Atualizar" (`ManagePixKeyListContainer.tsx:64-79`): só com a lista não vazia. */
export const pixKeysRefresh = action({
  id: 'bankAccount.pixKeys.refresh',
  kind: 'read',
  label: msg`Atualizar`,
  description: msg`Busca de novo as chaves Pix da conta aberta. Nada é alterado.`,
  run: () => undefined,
});

/** "Cadastrar chave Pix" (`ManagePixKeyForm.tsx:48-55`): abre "Registrar ou trazer chaves" no primeiro passo. */
export const pixKeysRegister = openOnly(
  'bankAccount.pixKeys.register',
  msg`Cadastrar chave Pix`,
  msg`Abre "Registrar ou trazer chaves" da conta aberta, no primeiro passo. Quem escolhe o tipo, digita a chave e confirma o cadastro (ou a portabilidade) é a pessoa: esta ferramenta não cadastra nada. Não recebe entrada.`,
);

/** "clique aqui" da faixa de pedidos recebidos (`ClaimsRequests.tsx:37-38`): só com pedido para esta conta. */
export const pixKeysClaims = openOnly(
  'bankAccount.pixKeys.claims',
  msg`Ver pedidos de portabilidade`,
  msg`Abre "Pedido de portabilidade da chave Pix" da conta aberta, com a lista de pedidos recebidos. Quem escolhe o pedido e confirma ou cancela a portabilidade é a pessoa: esta ferramenta não decide nada. Não recebe entrada.`,
);

export const bankAccountPixKeysScreen = defineScreen({
  id: 'bankAccount.pixKeys',
  path: `${BANK_ACCOUNT_LIST_PATH}/$bankAccountId/pix/gerenciar-chaves`,
  title: msg`Gerenciar chaves`,
  description: msg`As chaves Pix de uma conta digital: tipo e chave, data de criação e data de posse, com Atualizar, Cadastrar chave Pix (CPF ou CNPJ do titular, telefone, e-mail ou aleatória, com portabilidade quando a chave está em outra instituição), Excluir em cada linha e, quando há, os pedidos de portabilidade recebidos para confirmar ou cancelar.`,
  permission: can('BankAccount', 'Read'),
  place: { icon: 'wallet', parent: 'bankAccount.detail', record: msg`Gerenciar chaves` },
  actions: [pixKeysRefresh, pixKeysRegister, pixKeysClaims],
});
